import type { ReactNode } from "react";
import { FormattedMessage } from "react-intl";
import { useMatch } from "react-router-dom";
import { isPast } from "date-fns";

import { redirectToSubdomain } from "util/application_redirect";
import AppSubdomains from "constants/app_subdomains";
import { CertificateMigrationStatus, NotaryOnboardingStatuses } from "graphql_globals";
import { useOnboardingV2 } from "util/feature_detection";
import AlertMessage from "common/core/alert_message";
import { shouldMigrateToProofCertificate } from "common/notary/profile_wizard/section/digital_certificate/proof";
import Link from "common/core/link";

import type { NotaryProfileForOnboardingBanner_notaryProfile as NotaryProfile } from "./index_fragment.graphql";

type Props = {
  notaryProfile: NotaryProfile;
};

export function AppFrameNotaryOnboardingBanner({ notaryProfile }: Props) {
  const onboardingV2 = useOnboardingV2();
  const onboardingPath = onboardingV2
    ? "/settings/notary/onboarding/overview"
    : "/settings/notary/onboarding";
  const onboardingMatch = useMatch({ path: "/settings/notary/onboarding", end: false });
  if (
    onboardingMatch ||
    notaryProfile.onboardingStatus === NotaryOnboardingStatuses.COMPLETED ||
    notaryProfile.onboardingStatus === NotaryOnboardingStatuses.PAUSED
  ) {
    return null;
  }
  const handleClick = () => redirectToSubdomain(AppSubdomains.customer, onboardingPath);
  const certificateExpiration =
    notaryProfile.certificateMigrationStatus === CertificateMigrationStatus.PROOF_CERTIFICATE
      ? notaryProfile.proofCertificate?.validTo
      : notaryProfile.certExpiry;
  const shouldMigrateNotaries = shouldMigrateToProofCertificate(
    notaryProfile.usState.proofCertEnabled,
    certificateExpiration,
    notaryProfile.certificateMigrationStatus,
  );
  const isCertificateExpired = certificateExpiration && isPast(new Date(certificateExpiration));
  return isCertificateExpired ? (
    <AlertMessage
      automationId="notary-profile-expired-cert-alert-message"
      banner
      centerText
      kind="warning"
    >
      <FormattedMessage
        id="f20d6f84-3c27-40a3-bcd5-5dba2f8d5e31"
        defaultMessage="Your digital certificate has expired. Please <link>{actionText}</link>{additionalText}."
        values={{
          actionText: shouldMigrateNotaries ? "verify your identity" : "upload",
          additionalText: shouldMigrateNotaries
            ? " to issue a new digital certificate"
            : " a new digital certificate",
          link: (text) => (
            <Link
              onClick={(event) => {
                event.preventDefault();
                redirectToSubdomain(
                  AppSubdomains.customer,
                  shouldMigrateNotaries
                    ? "/settings/notary/onboarding/IdentityVerification"
                    : "/settings/notary/onboarding/DigitalCertificate",
                  { newTab: true },
                );
              }}
            >
              {text}
            </Link>
          ),
        }}
      />
    </AlertMessage>
  ) : (
    <AlertMessage
      automationId="notary-profile-incomplete-alert-message"
      banner
      centerText
      kind="warning"
    >
      <FormattedMessage
        id="76247766-1cd3-4d30-807d-d1f3001cd5fc"
        defaultMessage="Complete your notary profile to confirm that you are RON certified and start serving transactions. <profileLink>Complete my profile</profileLink>"
        values={{
          profileLink: (msg: ReactNode[]) => (
            <>
              &nbsp;
              <Link
                onClick={(evt) => {
                  evt.preventDefault();
                  handleClick();
                }}
              >
                {msg}
              </Link>
            </>
          ),
        }}
      />
    </AlertMessage>
  );
}
